import { z } from 'zod';
import { moneyAmountField } from './fields';

// ═══════════════════════════════════════════════════════════════
// ONBOARDING PERSONA MORAL (PM) — Origen de fondos (GAP 2, contrato #412)
// Endpoint regulatorio `POST …/origin-of-funds`, required [source,
// estimated_monthly_volume_cents]. Va tras beneficial_owners en moral_v1.
//
// REGLA DE DINERO (doc 08 / CLAUDE): el monto se captura en PESOS en el form y
// se convierte a CENTAVOS (entero) antes de enviarlo al BE — NUNCA se envía un
// float. La conversión vive en el form (`Money.fromDecimal(mxn).toCents()`); el
// schema solo valida el monto en pesos como número positivo.
//
// `source` tiene su PROPIO catálogo: una empresa no tiene salario ni remesas.
// Reusar el de Persona Física hacía que el BE rechazara con ONB_FUNDS_001
// ("Origen de fondos no válido"), y el catálogo remoto `funds_sources` tampoco
// sirve: es el de PF (entrega `business`, que tampoco es válido para PM).
// ═══════════════════════════════════════════════════════════════

/**
 * Orígenes de fondos válidos para Persona Moral, con los valores EXACTOS que
 * acepta `POST …/origin-of-funds`. NO son los de PF: `ingresos_operativos` es
 * el equivalente de `negocio` para una empresa.
 *
 * ⚠️ **LEGADO desde el 2026-08-27.** El contrato acepta ADEMÁS `source_code`,
 * los 20 códigos de `pm_funds_origins`, que es el vocabulario que viaja al
 * proveedor y el que el backend pide para capturas nuevas. Los dos campos
 * conviven y el backend NO traduce entre ellos: cada valor se guarda en su
 * columna y la otra queda nula. La pantalla de perfil transaccional migró a
 * `source_code`; estos slugs se conservan para expedientes ya capturados y
 * para `originOfFundsSchema`, que nadie más consume hoy.
 */
export const MORAL_FUNDS_SOURCES = [
  'ingresos_operativos',
  'inversiones',
  'ahorro',
  'financiamiento',
  'venta_activos',
  'herencia',
  'donacion',
  'otro',
] as const;

export const moralFundsSourceSchema = z.enum(MORAL_FUNDS_SOURCES, {
  errorMap: () => ({ message: 'Selecciona el origen de los fondos' }),
});

/**
 * Techo del volumen mensual estimado, en PESOS. El contrato lo expresa en
 * centavos (`estimated_monthly_volume_cents` ≤ 100,000,000,000), que son mil
 * millones de pesos. Se valida aquí para no gastar un round-trip en un
 * `ONB_FUNDS_002` que se puede atajar en el cliente.
 */
export const MORAL_MAX_MONTHLY_VOLUME_MXN = 1_000_000_000;

export const originOfFundsSchema = z.object({
  /** Origen de los recursos (→ `source`), en los 8 slugs legados. */
  source: moralFundsSourceSchema,
  /**
   * Volumen mensual estimado en PESOS, capturado como string vía `MoneyInput`
   * (→ se convierte a `estimated_monthly_volume_cents` en el form con
   * `Money.fromDecimal(parseMoneyToNumber(v)).toCents()`). `moneyAmountField`
   * valida que sea > 0 sin transformar (sigue siendo string para RHF).
   */
  estimatedMonthlyVolumeMxn: moneyAmountField({
    required: 'El monto es requerido',
    invalid: 'El monto debe ser mayor a cero',
    max: MORAL_MAX_MONTHLY_VOLUME_MXN,
    tooLarge: 'El monto excede el máximo permitido',
  }),
  /**
   * Descripción libre del origen de los recursos (→ `description`). Opcional en
   * el contrato: sólo se manda si trae contenido.
   */
  description: z.string().trim().max(500, 'La descripción es demasiado larga').optional(),
});

export type OriginOfFundsFormData = z.infer<typeof originOfFundsSchema>;

/**
 * Perfil transaccional de la empresa (diseño 2026-08-18). Extiende el origen de
 * fondos con cuatro campos que el contrato **todavía no acepta**: destino de
 * los recursos, rango de monto, rango de número de operaciones y frecuencia.
 * Se capturan y se conservan en el store de PM hasta que el backend los reciba;
 * ver docs/qa/pm-documentos-listado-sesion-2026-08-17.md.
 *
 * Los cuatro salen de catálogos del backend (`pm_*`) desde el 2026-08-27, así
 * que sus valores válidos ya no se declaran aquí. Todos son obligatorios: la
 * pantalla declara "es necesario llenar la información para continuar".
 */
/**
 * Un código de catálogo elegido por el usuario. Los catálogos PM
 * (`pm_funds_destinations`, `pm_operation_amount_ranges`,
 * `pm_operation_count_ranges`, `pm_transaction_frequencies`) los sirve el
 * backend en `GET /api/v1/catalogs`, así que **el conjunto válido ya no vive
 * aquí**: congelarlo en un `z.enum` haría que el día que cumplimiento agregue
 * una opción, el formulario rechazara una respuesta que el propio backend
 * ofreció.
 *
 * Lo que sí se valida en el cliente es que haya una elección. Que el código
 * pertenezca al catálogo lo garantiza el `<select>`: sólo puede devolver
 * uno de los valores que se pintaron.
 *
 * El `'0'` ("No asignado") NO llega hasta aquí: es un placeholder del backend y
 * el formulario lo filtra antes de pintarlo.
 */
const catalogChoiceField = (message: string) => z.string().min(1, message);

/**
 * "Otro origen, especificar" en `pm_funds_origins`. Es el único código del
 * catálogo que pide texto libre, y ese texto tiene destino propio en el
 * contrato: el `description` opcional de `origin-of-funds`.
 */
export const MORAL_FUNDS_ORIGIN_OTHER_CODE = '19';

export const transactionalProfileSchema = originOfFundsSchema
  .omit({ description: true })
  .extend({
    /**
     * Destino de los recursos (`pm_funds_destinations`, 19 opciones). Fue opcional
     * mientras su catálogo no existía —exigir un campo sin opciones dejaba la
     * pantalla inavanzable—; ahora que el backend lo sirve es obligatorio, como
     * el resto y como promete la pantalla.
     */
    fundsDestination: catalogChoiceField('Selecciona el destino de los recursos'),
    /** Rango del monto de operaciones (`pm_operation_amount_ranges`). */
    amountRange: catalogChoiceField('Selecciona el rango del monto de operaciones'),
    /** Rango del número de operaciones (`pm_operation_count_ranges`). */
    operationsRange: catalogChoiceField('Selecciona el rango del número de operaciones'),
    /**
     * Frecuencia de operaciones (`pm_transaction_frequencies`):
     * `1 Diaria · 2 Semanal · 3 Quincenal · 4 Mensual`.
     *
     * Es el MISMO catálogo que valida `expected_transaction_frequency` en
     * `POST …/company/transactional-profile`: acepta `1`–`4` y sólo rechaza el
     * `0` ("No asignado") con `ONB_TXPROFILE_003` (confirmado con backend
     * 2026-08-27). El día que ese endpoint tenga consumidor, el código se
     * reenvía tal cual: no hay traducción de por medio. La advertencia anterior
     * describía un catálogo del proveedor —`1 baja · 2 media · 3 alta`— que no
     * existe.
     */
    operationsFrequency: catalogChoiceField('Selecciona la frecuencia de operaciones'),
    /**
     * Origen de los recursos (`pm_funds_origins`, → `source_code`). Sustituye
     * al slug legado: es el vocabulario que viaja al proveedor.
     */
    source: catalogChoiceField('Selecciona el origen de los recursos'),
    /**
     * Texto libre del origen (→ `description`). Opcional en el contrato, pero
     * obligatorio aquí cuando el origen es "Otro origen, especificar": un
     * expediente regulado que declara "otro" sin decir cuál no declara nada.
     */
    description: z.string().trim().max(500, 'La descripción es demasiado larga').optional(),
  })
  .superRefine((data, ctx) => {
    if (data.source === MORAL_FUNDS_ORIGIN_OTHER_CODE && !data.description) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['description'],
        message: 'Describe el origen de los recursos',
      });
    }
  });

export type TransactionalProfileFormData = z.infer<typeof transactionalProfileSchema>;
